import { useContext, useMemo } from 'react';
import SimpleBar from 'simplebar-react';
import classNames from 'classnames';
import moment from 'moment';

import { IEtfDocumentBackend } from 'api/etf';

import { decodeSiteResolutionValue, SiteResolutionContext } from 'shared/mechanics/site-resolution';
import { ContentPanel, EtfLabel } from 'shared/ui-kit';
import { ContentPanelTable, ContentPanelTablePropsColumn } from 'shared/ui-kit/panel/content-panel-table';
import { AdsComponent } from 'shared/components/ads';

import styles from "./content.module.scss";

export interface EtfDocumentsPageContentProps {
	ticker: string;
	documents: IEtfDocumentBackend[];
}
export const EtfDocumentsPageContent = (props: EtfDocumentsPageContentProps) => {
	const desktopConcreteDocumentTableColumns: ContentPanelTablePropsColumn<IEtfDocumentBackend>[] = useMemo(
		() => [
			{ id: 'doc', title: "Документ", getValue: doc => doc.name, renderCell: document => <a href={document.url}>{ document.name }</a>, headerClassName: styles.header_text },
			{ id: 'date', title: "Дата", getValue: doc => moment(doc.date).format('D MMM YYYY'), cellClassName: styles.cell_date, headerClassName: styles.cell_date }
		],
		[]);
	const desktopOthersDocumentTableColumns: ContentPanelTablePropsColumn<IEtfDocumentBackend>[] = useMemo(
		() => [
			{ id: 'document', title: "Документ", getValue: doc => doc.name, renderCell: document => <a href={document.url}>{ document.name }</a>, headerClassName: styles.header_text},
			{ id: 'category', title: "Категория", getValue: doc => doc.category, cellClassName: styles.cell_category, headerClassName: styles.header_text },
			{ id: 'date', title: "Дата", getValue: doc => moment(doc.date).format('D MMM YYYY'), cellClassName: styles.cell_date, headerClassName: styles.cell_date }
		],
		[]);
	const compactDocumentsTableColumns: ContentPanelTablePropsColumn<IEtfDocumentBackend>[] = useMemo(
		() => [
			{ 
				id: 'doc',
				title: "Документ",
				getValue: doc => doc.name,
				renderCell: document => (
					<>
						<p><a href={document.url}>{ document.name }</a></p>
						<p>{ moment(document.date).format('D MMM YYYY') }</p>
					</>
				)
			},
		],
		[]);
	const siteResolution = useContext(SiteResolutionContext);

	const concreteDocumentTableColumns = decodeSiteResolutionValue(siteResolution, desktopConcreteDocumentTableColumns, compactDocumentsTableColumns);
	const othersDocumentTableColumns = decodeSiteResolutionValue(siteResolution, desktopOthersDocumentTableColumns, compactDocumentsTableColumns);

	const groupedDocuments = useMemo(() => {
		const sortedDocuments = props.documents.sort((doc1, doc2) => doc2.date.getTime() - doc1.date.getTime());

		const rules = sortedDocuments.filter(doc => doc.category.trim().toLocaleLowerCase() === 'правила');
		const reports = sortedDocuments.filter(doc => doc.category.trim().toLocaleLowerCase() === 'отчетность');
		const other = sortedDocuments.filter(doc => rules.indexOf(doc) === -1 && reports.indexOf(doc) === -1);

		return { rules, reports, other }
	},
	[props.documents]);

	const ads2Key = decodeSiteResolutionValue(siteResolution, 'InfoDetailedHorizontal2:desktop', 'InfoDetailedHorizontal2:mobile');

	return (
		<div className={classNames("flex-size-fill flex-container-vertical", styles.container)}>
			<div className={classNames("flex-size-least", styles.header)}>
				<h1>Правила и отчетость фонда <EtfLabel ticker={props.ticker} /></h1>
			</div>
			<div className={classNames("flex-size-fill", styles.content)}>
				<SimpleBar style={{ width: '100%', height: '100%'}}>
					<ContentPanel title="Правила" className={styles.item}>
						<ContentPanelTable columns={concreteDocumentTableColumns} rows={groupedDocuments.rules} />
					</ContentPanel>
					<div className={styles.item}>
						<AdsComponent id="InfoDetailedHorizontal1" isVertical={false} />
					</div>
					<ContentPanel title="Отчетность" className={styles.item}>
						<ContentPanelTable columns={concreteDocumentTableColumns} rows={groupedDocuments.reports} />
					</ContentPanel>
					<div className={styles.item}>
						<AdsComponent id={ads2Key} isVertical={false} />
					</div>
					<ContentPanel title="Иное" className={styles.item}>
						<ContentPanelTable columns={othersDocumentTableColumns} rows={groupedDocuments.other} />
					</ContentPanel>
				</SimpleBar>
			</div>
		</div>
	);
}